import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { PiArrowLeft, PiList, PiPlus, PiTableLight } from 'react-icons/pi';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Route as SettingsCustomFieldsRoute } from 'src/routes/settings/custom-fields';
import { settingsSearchSchema } from 'src/modules/settings/schemas/settings.schema';
import { useFunnels } from 'src/modules/shared/hooks/api/crm/funnels/funnels';
import { CustomFieldType, CustomFieldEntityType } from 'src/api/generated';
import { CustomFieldDefinitionsList } from '../organisms/custom-fields/custom-field-definitions-list';
import { CustomFieldDefinitionForm } from '../organisms/custom-fields/custom-field-definition-form';
import {
  CustomFieldListOptionsManager,
  type CustomFieldListOptionsManagerRef,
} from '../organisms/custom-fields/custom-field-list-options-manager';
import { useCustomFieldDefinition } from 'src/modules/shared/hooks/api/custom-fields/definitions';
import Loading from 'src/modules/shared/components/atoms/loading';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { useFunnelCustomFieldDefinitions } from 'src/modules/shared/hooks/api/crm/funnels/funnel-custom-field-definitions';
import {
  CustomFieldsSidebar,
  type CustomFieldsSidebarSelection,
} from '../molecules/custom-fields/custom-fields-sidebar';
import { useUnsavedNavigationGuardState } from 'src/modules/shared/hooks/unsaved-navigation-guard';
import { UnsavedChangesModal } from 'src/modules/shared/components/molecules/modals/unsaved-changes-modal';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { CrmCustomFieldPermission } from 'src/modules/shared/types/permissions.type';

type CustomFieldsView = 'list' | 'create' | 'edit' | 'options';
type CustomFieldsScope = 'all' | 'funnel';
type CustomFieldsEntityKey = 'CONTACT' | 'FUNNEL_CONVERSATION' | 'PRODUCT';

const DEFAULT_ENTITY: CustomFieldsEntityKey = 'FUNNEL_CONVERSATION';

export const CustomFieldsSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: SettingsCustomFieldsRoute.id });
  const scope: CustomFieldsScope = search.scope ?? 'all';
  const view: CustomFieldsView = search.view ?? 'list';
  const funnelId =
    typeof search.funnelId === 'number' ? search.funnelId : undefined;
  const entityFromSearch = search.entity as CustomFieldsEntityKey | undefined;
  const entity: CustomFieldsEntityKey = entityFromSearch ?? DEFAULT_ENTITY;
  const definitionId =
    typeof search.definitionId === 'number' ? search.definitionId : undefined;

  const [shouldNavigateAfterSave, setShouldNavigateAfterSave] =
    useState<boolean>(false);

  const guard = useUnsavedNavigationGuardState(false);
  const { funnels = [] } = useFunnels();
  const { hasPermission: canUpdateDefinition } = useHasPermissions(
    CrmCustomFieldPermission.Update
  );

  const selection: CustomFieldsSidebarSelection =
    scope === 'funnel' && funnelId
      ? { type: 'funnel', funnelId }
      : {
          type: 'type',
          value: entityFromSearch ?? 'ALL',
        };

  const listOptionsManagerRef = useRef<CustomFieldListOptionsManagerRef | null>(
    null
  );

  const updateSearch = useCallback(
    (next: Partial<typeof search>) => {
      void navigate({
        to: SettingsCustomFieldsRoute.to,
        search: (prev) =>
          settingsSearchSchema.parse({
            ...prev,
            ...next,
            subtab: 'custom-fields',
          }),
      });
    },
    [navigate]
  );

  const handleSelectFunnel = (nextFunnelId: number) => {
    updateSearch({
      scope: 'funnel',
      funnelId: nextFunnelId,
      view: 'list',
    });
  };

  const handleCreateNew = () => {
    updateSearch({
      view: 'create',
      definitionId: undefined,
    });
  };

  const navigateToList = useCallback(
    () =>
      updateSearch({
        view: 'list',
        definitionId: undefined,
      }),
    [updateSearch]
  );

  const handleAfterSave = () => {
    guard.setHasUnsavedChanges(false);
    setShouldNavigateAfterSave(true);
  };

  useEffect(() => {
    if (!shouldNavigateAfterSave || guard.hasUnsavedChanges) {
      return;
    }

    navigateToList();
    setShouldNavigateAfterSave(false);
  }, [guard.hasUnsavedChanges, navigateToList, shouldNavigateAfterSave]);

  const handleBackToList = () => {
    navigateToList();
  };

  const isEditView = view === 'edit';
  const isOptionsView = view === 'options';

  // TODO: check when implementing i18n
  const headerTitle = isEditView
    ? 'Editar campo'
    : isOptionsView
      ? 'Opciones del campo'
      : 'Campos personalizados';

  const showBackButton = isEditView || isOptionsView;

  const {
    definition: activeDefinition,
    loading: loadingDefinition,
    error: activeDefinitionError,
  } = useCustomFieldDefinition(definitionId ?? 0, !!definitionId);
  const isSystemManagedPropertyDefinition =
    activeDefinition?.entityType === CustomFieldEntityType.PROPERTY;

  const {
    definitions: funnelDefinitions,
    loading: loadingFunnelDefinitions,
    error: funnelDefinitionsError,
  } = useFunnelCustomFieldDefinitions({
    funnelId,
    enabled: scope === 'funnel' && view === 'list' && !!funnelId,
  });

  const renderOptions = () => {
    if (!definitionId) {
      return (
        <ErrorState
          // TODO: check when implementing i18n
          title="Campo no encontrado"
          // TODO: check when implementing i18n
          description="No se ha especificado un campo válido para gestionar sus opciones."
        />
      );
    }
    if (isSystemManagedPropertyDefinition) {
      return (
        <ErrorState
          title="Campo no disponible"
          description="Este campo es gestionado por el sistema y no puede editarse desde esta pantalla."
        />
      );
    }
    if (loadingDefinition) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loading size="lg" />
        </div>
      );
    }
    if (activeDefinitionError || !activeDefinition) {
      return (
        <ErrorState
          // TODO: check when implementing i18n
          title="Error cargando el campo"
          description="No se pudo cargar el campo para gestionar sus opciones."
        />
      );
    }
    return (
      <CustomFieldListOptionsManager
        ref={listOptionsManagerRef}
        definition={activeDefinition}
      />
    );
  };

  const renderAllFields = () => {
    if (isOptionsView) {
      return renderOptions();
    }
    if (view === 'create' || view === 'edit') {
      if (view === 'edit' && isSystemManagedPropertyDefinition) {
        return (
          <ErrorState
            title="Campo no disponible"
            description="Este campo es gestionado por el sistema y no puede editarse desde esta pantalla."
          />
        );
      }
      return (
        <CustomFieldDefinitionForm
          entityType={entity as CustomFieldEntityType}
          definitionId={view === 'edit' ? definitionId : null}
          onSave={handleAfterSave}
          onCancel={handleBackToList}
          onEditListOptions={(definition) =>
            updateSearch({
              view: 'options',
              definitionId: definition.id,
            })
          }
          showFunnelMapping
          allowEntityChange={view === 'create'}
          onDirtyChange={guard.setHasUnsavedChanges}
        />
      );
    }
    if (entityFromSearch) {
      return (
        <CustomFieldDefinitionsList
          entityType={entity as CustomFieldEntityType}
          onEdit={(id) =>
            updateSearch({
              view: 'edit',
              definitionId: id,
            })
          }
        />
      );
    }
    return (
      <CustomFieldDefinitionsList
        onEdit={(id) =>
          updateSearch({
            view: 'edit',
            definitionId: id,
          })
        }
      />
    );
  };

  const renderFunnelFields = () => {
    if (!funnelId) {
      return (
        <div className="h-full flex items-center justify-center">
          <Text size="small" color="secondary">
            {/* TODO: check when implementing i18n */}
            Selecciona un embudo para ver sus campos personalizados.
          </Text>
        </div>
      );
    }
    if (isOptionsView) {
      return renderOptions();
    }
    if (view === 'create' || view === 'edit') {
      if (view === 'edit' && isSystemManagedPropertyDefinition) {
        return (
          <ErrorState
            title="Campo no disponible"
            description="Este campo es gestionado por el sistema y no puede editarse desde esta pantalla."
          />
        );
      }
      return (
        <CustomFieldDefinitionForm
          entityType={entity as CustomFieldEntityType}
          definitionId={view === 'edit' ? definitionId : null}
          onSave={handleAfterSave}
          onCancel={handleBackToList}
          onEditListOptions={(definition) =>
            updateSearch({
              view: 'options',
              definitionId: definition.id,
            })
          }
          initialFunnelId={funnelId}
          onDirtyChange={guard.setHasUnsavedChanges}
        />
      );
    }
    if (loadingFunnelDefinitions) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loading size="lg" />
        </div>
      );
    }
    if (funnelDefinitionsError) {
      return (
        <ErrorState
          title="Error cargando campos"
          description="No se pudieron cargar los campos personalizados del embudo seleccionado."
        />
      );
    }
    return (
      <CustomFieldDefinitionsList
        definitions={funnelDefinitions}
        funnelId={funnelId}
        onEdit={(id) =>
          updateSearch({
            view: 'edit',
            definitionId: id,
          })
        }
      />
    );
  };

  const renderPrimaryAction = () => {
    const isListEdit =
      isEditView &&
      !!definitionId &&
      activeDefinition?.fieldType === CustomFieldType.LIST;

    if (isOptionsView) {
      return (
        <Button
          onClick={() => {
            if (listOptionsManagerRef.current) {
              listOptionsManagerRef.current.handleCreateNew();
            }
          }}
          variant="primary"
          size="md"
          className="flex items-center gap-2"
        >
          <PiPlus className="w-4 h-4" />
          {/* TODO: check when implementing i18n */}
          <Text size="small">Nueva opción</Text>
        </Button>
      );
    }

    if (isListEdit && canUpdateDefinition) {
      return (
        <Button
          onClick={() => {
            if (!definitionId) return;
            updateSearch({
              view: 'options',
              definitionId,
            });
          }}
          variant="primary"
          size="md"
          className="flex items-center gap-2"
        >
          <PiList className="w-4 h-4" />
          {/* TODO: check when implementing i18n */}
          <Text size="small">Gestionar opciones</Text>
        </Button>
      );
    }

    if (isEditView) {
      return null;
    }

    return (
      <Button
        onClick={handleCreateNew}
        variant="primary"
        size="md"
        className="flex items-center gap-2"
      >
        <PiPlus className="w-4 h-4" />
        <Text size="small" className="hidden md:inline">
          {/* TODO: check when implementing i18n */}
          Nuevo campo
        </Text>
      </Button>
    );
  };

  return (
    <>
      <div className="flex flex-col h-full">
        <Panel className="h-full flex flex-col">
          <PanelHeader className="px-4 py-3 border-b">
            <div className="flex justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                {showBackButton && (
                  <Button
                    variant="neutral"
                    appearance="ghost"
                    size="sm"
                    onClick={handleBackToList}
                    isIconOnly
                  >
                    <PiArrowLeft className="w-5 h-5" />
                  </Button>
                )}
                {!showBackButton && <PiTableLight className="w-8 h-8" />}
                <Text as="h2" size="h2" weight="medium" className="truncate">
                  {headerTitle}
                </Text>
              </div>

              <div className="flex items-center gap-2">
                {renderPrimaryAction()}
              </div>
            </div>
          </PanelHeader>

          <div className="flex flex-col lg:flex-row flex-1 min-h-0">
            <CustomFieldsSidebar
              funnels={funnels.map((f) => ({ id: f.id, name: f.name }))}
              selection={selection}
              onSelectType={(value) => {
                if (value === 'ALL') {
                  updateSearch({
                    scope: 'all',
                    entity: undefined,
                    funnelId: undefined,
                    view: 'list',
                    definitionId: undefined,
                  });
                } else {
                  updateSearch({
                    scope: 'all',
                    entity: value,
                    funnelId: undefined,
                    view: 'list',
                    definitionId: undefined,
                  });
                }
              }}
              onSelectFunnel={handleSelectFunnel}
            />

            <div className="flex-1 overflow-auto p-3 sm:pb-6 sm:pt-11 w-full">
              {scope === 'all' && (
                <div className="space-y-3 sm:space-y-4 h-full flex flex-col">
                  <div className="flex-1 min-h-0 overflow-auto">
                    {renderAllFields()}
                  </div>
                </div>
              )}

              {scope === 'funnel' && (
                <div className="h-full">{renderFunnelFields()}</div>
              )}
            </div>
          </div>
        </Panel>
      </div>

      <UnsavedChangesModal
        isOpen={guard.isBlocked}
        onProceed={guard.proceed}
        onStay={guard.stay}
      />
    </>
  );
};
